import 'rxjs/add/observable/fromPromise'
import 'rxjs/add/observable/of'
import 'rxjs/add/operator/catch'
import 'rxjs/add/operator/concatAll'
import 'rxjs/add/operator/concatMap'
import 'rxjs/add/operator/distinctUntilChanged'
import 'rxjs/add/operator/filter'
import 'rxjs/add/operator/first'
import 'rxjs/add/operator/map'

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Storage } from '@ionic/storage'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { Observable } from 'rxjs/Observable'
import { Subject } from 'rxjs/Subject'

import { AuthToken, UserAuthConfig, UserState } from './user-auth-models'

export type UserPermissionFilter<UserType> = (user: UserType) => Observable<boolean>

@Injectable()
export class UserAuthProvider<UserType> {

  private refreshedToken: Subject<AuthToken>
  private authStateSubject: BehaviorSubject<boolean>
  private userStateSubject: BehaviorSubject<UserState<UserType>>

  private isRefreshing: boolean;

  private permissionFilter: UserPermissionFilter<UserType> = null

  private lastUserState: UserState<UserType>
  public currentUser: UserType

  constructor(public http: HttpClient, public storage: Storage, public config: UserAuthConfig) {
    if (config) {
      console.log('UserAuth init with config')
    } else {
      console.error('!! UserAuth init without config');
    }
    this.refreshedToken = new Subject<AuthToken>()
    this.authStateSubject = new BehaviorSubject<boolean>(null)
    this.userStateSubject = new BehaviorSubject<UserState<UserType>>(null)
    storage.ready().then(() => {
      this.getAuthToken().subscribe(
        token => {
          console.log("Token from Storage: ")
          console.log(token)
          this.authStateSubject.next(token && token.access_token ? true : false)
        },
        _e => {
          this.authStateSubject.next(false)
        }
      )
    })

    this.authStateSubject
      .filter(x => x != null)
      .distinctUntilChanged()
      .concatMap(state => state ? this.getUserFromApi() : Observable.of(null))
      .map(user => {
        return {
          logged: user != null,
          user: user
        } as UserState<UserType>
      })
      .concatMap(userState => {
        if (userState.logged && this.permissionFilter != null) {
          return this.permissionFilter(userState.user)
            .catch(_e => Observable.of(false))
            .concatMap(has_permission => {
              userState.has_permission = has_permission
              return userState.has_permission ? Observable.of(userState)
                : this.removeAuthToken().concatMap(_ => Observable.of(userState))
            })
        } else {
          userState.has_permission = userState.logged
          return Observable.of(userState)
        }
      })
      .subscribe(userState => {
        this.pushNewUserState(userState)
      },
      e => {
        console.error(e)
        this.pushNewUserState(null)
      },
      () => {
        console.log("AUTH STATE - ON COMPLETE")
      })
  }

  private getUserFromApi(): Observable<UserType> {
    return this.http.get(this.config.userPath)
      .catch((_res: HttpErrorResponse) => Observable.of(null))
      .map(data => data instanceof Array ? data[0] || null : data)
  }

  /**
   *
   * @param userState
   */
  private pushNewUserState(userState: UserState<UserType>) {
    this.lastUserState = userState || { user: null, logged: false, has_permission: false }
    this.currentUser = userState.user
    console.log("Push new User State: Logged " + this.lastUserState.logged + " - Permission: " + this.lastUserState.has_permission)
    this.userStateSubject.next(this.lastUserState)
  }

  /**
   * Notifica l'ultimo e i successivi stati dell'utente, filtrati per filterPermission se impostato
   */
  get userState() {
    return this.userStateSubject
      .filter(x => x != null)
  }

  /**
   * Imposta un filtro che determina le condizioni per permettere all'utente di accedere.
   * In caso di esito negativo, il token salvato in precedenza viene eliminato
   * @param filter Filtro
   */
  public setPermission(filter: UserPermissionFilter<UserType>) {
    this.permissionFilter = filter
  }

  /**
   * Rimuove eventuali filtri/permessi per l'accesso
   */
  public clearPermission() {
    this.permissionFilter = null
  }

  /**
   * Effettua il login con email e password. Da on next se riesce ad ottenere un token.
   * Per avere lo stato completo dell'utente utilizzare userState
   * @param email
   * @param password
   */
  public login(email: string, password: string): Observable<AuthToken> {
    let headers = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') }
    let body = "client_id=" + this.config.clientID
      + "&client_secret=" + this.config.clientSecret
      + "&grant_type=" + "password"
      + "&password=" + password
      + "&username=" + email;
    const req = this.http.post(this.config.authPath, body, headers) as Observable<AuthToken>
    return req.concatMap(refreshedToken => {
      console.warn('---O SUCCESS TOKEN: ' + refreshedToken.access_token)
      return this.saveAuthToken(refreshedToken)
      // .concatMap(token => {
      //   return this.userState.first()
      // })
    })
  }

  /**
   * Cancella il token effettuando quindi il logout
   * @returns Observable che notifica il compimento dell'azione
   */
  public logout(): Observable<AuthToken> {
    return this.removeAuthToken()
  }

  /**
   * Cancella il token effettuando quindi il logout. No subscription required
   * @deprecated
   */
  public signOut() {
    this.removeAuthToken().subscribe()
  }

  public getAuthToken(): Observable<AuthToken> {
    return Observable.fromPromise(this.storage.get('auth_token'))
      .map(data => JSON.parse(data))
      //Strateggia per anticipare refresh del token
      .concatMap(authToken => {
        if (Date.now() < authToken.expireTime) {
          return Observable.of(authToken)
        } else {
          console.warn("Token EXPIRED")
          return this.syncronizeRefreshToken(authToken)
        }
      })
      .catch(_error => {
        return Observable.of(null)

      }) as Observable<AuthToken>
  }

  public syncronizeRefreshToken(expiredToken: AuthToken): Observable<AuthToken> {
    console.warn(`Call Refreshing TOKEN FOR: ${expiredToken.access_token}`)
    if (!this.isRefreshing) {
      this.isRefreshing = true
      console.warn(`> START Refreshing Token: ${expiredToken.access_token}`)

      if (expiredToken.refresh_token) {

        let body = "client_id=" + this.config.clientID
          + "&client_secret=" + this.config.clientSecret
          + "&grant_type=" + "refresh_token"
          + "&refresh_token=" + expiredToken.refresh_token;
        let headers = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') };

        let req = this.http.post(this.config.authPath, body, headers) as Observable<AuthToken>
        return req.concatMap(refreshedToken => {
          console.warn('@ SUCCESS Refreshed Token: ' + refreshedToken.access_token)
          return this.saveAuthToken(refreshedToken)
        }).catch(_error => {
          console.warn('X FAIL Refreshing Token')
          return this.removeAuthToken().map(() => {
            this.isRefreshing = false
            return {}
          })
        }) as Observable<AuthToken>
      }
      else {
        console.warn("X FAIL Can't Find Refresh Token for: " + expiredToken.access_token)

        return this.removeAuthToken().map(() => {
          this.isRefreshing = false
          return {}
        })
      }
    }
    else {
      return this.refreshedToken.first()
    }
  }

  private saveAuthToken(authToken: AuthToken): Observable<AuthToken> {
    authToken.expireTime = Date.now() + authToken.expires_in * 1000
    authToken.authorization = authToken.token_type + " " + authToken.access_token
    return Observable.fromPromise(this.storage.set('auth_token', JSON.stringify(authToken))).map(() => {
      console.warn(`- SAVED NEW TOKEN --- ${authToken.authorization}`)
      this.isRefreshing = false
      this.refreshedToken.next(authToken)
      this.authStateSubject.next(true)
      return authToken
    })
  }

  private removeAuthToken(): Observable<AuthToken> {
    return Observable.fromPromise(this.storage.remove('auth_token')).map(_x => {
      console.warn(`- DELETED TOKEN ---`)
      this.authStateSubject.next(false)
      return {}
    })
  }


}



// WEBPACK FOOTER //
// ./src/modules/mrk-user-auth/user-auth.ts